import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { BareInput } from "components/DesignSystem/TextInput/TextInput";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { FormikForm } from "components/FormikForm/FormikForm";
import { RightArrow } from "components/icons/RightArrow";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { Hash } from "components/icons/Hash";
import { NumericFormat } from "react-number-format";
import { SingleValue } from "react-select";
import {
  FORMULA_NEEDS_VALUE,
  FORMULA_NEEDS_DATE_WINDOW,
  FORMULA_IS_LAST_6M,
  FORMULA_IS_MULTIPLIER,
  FORMULA_OPTIONS,
  FormulaValue,
  FORMULA,
  FORMULA_INFO,
} from "constants/forecast";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import ToolTip from "components/design/toolTip";
import { SmallInfo } from "components/icons/SmallInfo";
import classNames from "classnames";

type Props = {
  formula: FormulaValue;
  value: string;
  startDate: string;
  endDate: string;
  layout?: "row" | "column";
  disabled?: boolean;
  onFormulaChange: (formula: FormulaValue, value: string) => void;
  onValueChange: (value: string) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
};

const FIXED_GROWTH_SENTINEL = "FIXED_GROWTH";

const HIDDEN_FORMULA_OPTIONS = new Set<FormulaValue>([
  FORMULA.GROWTH_PCT,
  FORMULA.GROWTH_FIXED,
  FORMULA.NO_RULE,
  FORMULA.CUSTOM_FIXED,
  FORMULA.FORECAST_LINEAR_LAST_6M,
  FORMULA.FORECAST_ETS_LAST_6M,
  FORMULA.FORECAST_ETS_CUSTOM,
  FORMULA.TREND_LAST_6M,
  FORMULA.TREND_CUSTOM,
  FORMULA.AVG_LAST_6M,
  FORMULA.AVG_CUSTOM,
  FORMULA.GROWTH_LAST_6M,
  FORMULA.GROWTH_CUSTOM,
]);

const FormulaOption = (props: {
  innerProps: React.HTMLAttributes<HTMLDivElement>;
  label: string;
  data: OptionData;
  isFocused: boolean;
  isSelected: boolean;
}) => {
  const { innerProps, label, data, isFocused, isSelected } = props;
  const info = FORMULA_INFO[data.value as FormulaValue];

  return (
    <div
      {...innerProps}
      className={classNames(
        "t-flex t-items-center t-justify-between t-gap-2 t-px-3 t-py-2 t-cursor-pointer",
        {
          "t-bg-surface-purple": isSelected,
          "t-bg-surface-lighter-grey": isFocused && !isSelected,
        }
      )}
    >
      <span className="t-text-body-sm">{label}</span>
      {info && (
        <ToolTip
          text={
            <div className="t-flex t-flex-col t-gap-1 t-text-body-sm t-text-left">
              <p>{info.description}</p>
              <p>{info.howItWorks}</p>
            </div>
          }
          side="right"
          propClass="t-max-w-72"
        >
          <span
            className="t-flex t-text-text-30 t-items-center t-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <SmallInfo className="t-size-3.5" />
          </span>
        </ToolTip>
      )}
    </div>
  );
};

const last6MonthsRange = () => {
  const end = dayjs().subtract(1, "month").endOf("month");
  const start = end.subtract(5, "month").startOf("month");
  return { start: start.format(YYYY_MM_DD), end: end.format(YYYY_MM_DD) };
};

export const ForecastRuleFields = ({
  formula,
  value,
  startDate,
  endDate,
  layout = "row",
  disabled = false,
  onFormulaChange,
  onValueChange,
  onStartDateChange,
  onEndDateChange,
}: Props) => {
  const isNoRule = formula === FORMULA.NO_RULE;
  const needsValue = FORMULA_NEEDS_VALUE.has(formula);
  const needsDateWindow = FORMULA_NEEDS_DATE_WINDOW.has(formula);
  const isLast6M = FORMULA_IS_LAST_6M.has(formula);
  const isMultiplier = FORMULA_IS_MULTIPLIER.has(formula);
  const isFixedGrowth =
    formula === FORMULA.GROWTH_PCT || formula === FORMULA.GROWTH_FIXED;

  const last6M = last6MonthsRange();

  const baseOptions: { value: string; label: string }[] =
    FORMULA_OPTIONS.filter((opt) => !HIDDEN_FORMULA_OPTIONS.has(opt.value)).map(
      (opt) => ({ value: opt.value as string, label: opt.label })
    );

  const formulaOptions = [
    { value: FORMULA.NO_RULE, label: "No rule" },
    { value: FIXED_GROWTH_SENTINEL, label: "Growth rate" },
    ...baseOptions,
  ];

  const comboboxValue = isFixedGrowth
    ? { value: FIXED_GROWTH_SENTINEL, label: "Growth rate" }
    : formulaOptions.find((o) => o.value === formula) || null;

  const handleFormulaChange = (
    opt: SingleValue<OptionData> | readonly OptionData[]
  ) => {
    const selected = Array.isArray(opt)
      ? null
      : (opt as SingleValue<OptionData>);
    if (selected?.value === FIXED_GROWTH_SENTINEL) {
      onFormulaChange(FORMULA.GROWTH_PCT, "");
      return;
    }
    onFormulaChange(selected?.value as FormulaValue, "");
  };

  const handleGrowthTypeToggle = (toggleValue: string) => {
    const next =
      toggleValue === "MULTIPLIER" ? FORMULA.GROWTH_PCT : FORMULA.GROWTH_FIXED;
    onFormulaChange(next, "");
  };

  const isColumn = layout === "column";

  const formulaCombobox = (
    <Combobox
      options={formulaOptions}
      value={comboboxValue}
      onChange={handleFormulaChange}
      isClearable={false}
      isDisabled={disabled}
      size={isColumn ? "small" : "regular"}
      menuPortalTarget={document.body}
      components={{ Option: FormulaOption }}
      styles={{
        menuPortal: (base) => ({ ...base, width: 280 }),
      }}
    />
  );

  if (isNoRule) {
    return (
      <div
        className={classNames({
          "t-w-full": isColumn,
          "t-w-[266px] t-flex-shrink-0": !isColumn,
        })}
      >
        {formulaCombobox}
      </div>
    );
  }

  const formulaField = (
    <div
      className={classNames({
        "t-w-full": isColumn,
        "t-w-[266px] t-flex-shrink-0": !isColumn,
      })}
    >
      {formulaCombobox}
    </div>
  );

  const valueField = needsValue && (
    <div
      className={classNames({
        "t-w-full": isColumn,
        "t-w-80 t-flex-shrink-0": !isColumn,
      })}
    >
      <NumericFormat
        value={value}
        placeholder={isMultiplier ? "1.10" : "0.00"}
        decimalScale={isMultiplier ? 4 : 2}
        customInput={BareInput}
        customSize={isColumn ? "small" : "regular"}
        {...(isMultiplier
          ? { suffix: "x" }
          : { prefix: "$", thousandSeparator: true })}
        onValueChange={({ value: v }) => onValueChange(v)}
        rightComponent={
          isFixedGrowth ? (
            <div className="-t-mr-2 t-py-1">
              <ToggleGroup
                variant="primary"
                size="small"
                value={isMultiplier ? "MULTIPLIER" : "ABSOLUTE"}
                onValueChange={handleGrowthTypeToggle}
              >
                <ToggleGroupItem
                  value="MULTIPLIER"
                  title="Multiplier (e.g. 1.10 = 10% growth)"
                >
                  x
                </ToggleGroupItem>
                <ToggleGroupItem value="ABSOLUTE">
                  <Hash />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          ) : undefined
        }
      />
    </div>
  );

  const dateFields = (needsDateWindow || isLast6M) && (
    <FormikForm
      enableReinitialize
      initialValues={{
        startDate: isLast6M ? last6M.start : startDate,
        endDate: isLast6M ? last6M.end : endDate,
      }}
      onSubmit={() => {}}
    >
      <div className="t-flex t-flex-row t-gap-2 t-items-center">
        <div
          className={classNames({
            "t-flex-1": isColumn,
            "t-w-36 t-flex-shrink-0": !isColumn,
          })}
        >
          <DateField
            name="startDate"
            placeholder="Start date"
            disabled={isLast6M}
            maxDate={endDate && !isLast6M ? new Date(endDate) : undefined}
            onDateChange={(date) =>
              onStartDateChange(dayjs(date).format(YYYY_MM_DD))
            }
          />
        </div>
        <span className="t-text-text-30">
          <RightArrow />
        </span>
        <div
          className={classNames({
            "t-flex-1": isColumn,
            "t-w-36 t-flex-shrink-0": !isColumn,
          })}
        >
          <DateField
            name="endDate"
            placeholder="End date"
            disabled={isLast6M}
            minDate={startDate && !isLast6M ? new Date(startDate) : undefined}
            onDateChange={(date) =>
              onEndDateChange(dayjs(date).format(YYYY_MM_DD))
            }
          />
        </div>
      </div>
    </FormikForm>
  );

  if (isColumn) {
    return (
      <div className="t-flex t-flex-col t-gap-3">
        {formulaField}
        {valueField}
        {dateFields}
      </div>
    );
  }

  return (
    <div className="t-flex t-items-center t-gap-2">
      {formulaField}
      {valueField}
      {dateFields}
    </div>
  );
};
